/**
 * Remote capture controls: the organization's default tracking schedule and
 * time zone, and each device's pause, end date and overrides. The Hub merges
 * them and the agent enforces the result, so off hours are never recorded.
 * These helpers mirror the Hub's validation so mistakes are caught in the form.
 */
export const NO_DEVICE_CONTROLS = {
    paused: false,
    ends_at: null,
    timezone: null,
    schedule: null,
};
export const WEEKDAYS = [
    { value: 1, short: 'Mon' },
    { value: 2, short: 'Tue' },
    { value: 3, short: 'Wed' },
    { value: 4, short: 'Thu' },
    { value: 5, short: 'Fri' },
    { value: 6, short: 'Sat' },
    { value: 7, short: 'Sun' },
];
/** The window a new schedule starts with. */
export function workingHours() {
    return { days: [1, 2, 3, 4, 5], start: '09:00', end: '17:00' };
}
/** Matches the Hub's limit. */
export const MAX_WINDOWS = 28;
const MINUTES_PER_DAY = 24 * 60;
/** "HH:MM" to minutes since midnight, or null when malformed. */
export function parseMinutes(value) {
    const match = /^(\d{2}):(\d{2})$/.exec(value);
    if (!match)
        return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const total = hours * 60 + minutes;
    return minutes < 60 && total <= MINUTES_PER_DAY ? total : null;
}
/** What's wrong with these windows, or null when the Hub will accept them. */
export function scheduleProblem(windows) {
    if (windows.length === 0)
        return 'Add at least one tracking window, or pause the device instead.';
    if (windows.length > MAX_WINDOWS)
        return `Use at most ${MAX_WINDOWS} windows.`;
    for (const slot of windows) {
        if (slot.days.length === 0)
            return 'Pick at least one day for each window.';
        const start = parseMinutes(slot.start);
        const end = parseMinutes(slot.end);
        if (start === null || end === null)
            return 'Enter times as HH:MM.';
        if (start >= end) {
            return 'Each window must end after it starts. Split overnight shifts into two windows.';
        }
    }
    return null;
}
/** "Mon–Fri", "Sat, Sun", "Mon, Wed–Fri". */
export function describeDays(days) {
    const sorted = [...new Set(days)].sort((a, b) => a - b);
    const runs = [];
    for (const day of sorted) {
        const run = runs[runs.length - 1];
        if (run && day === run[run.length - 1] + 1)
            run.push(day);
        else
            runs.push([day]);
    }
    const name = (day) => WEEKDAYS[day - 1]?.short ?? '?';
    return runs
        .map(run => run.length > 2 ? `${name(run[0])}–${name(run[run.length - 1])}` : run.map(name).join(', '))
        .join(', ');
}
export function describeSchedule(schedule) {
    if (!schedule || schedule.mode === 'always')
        return 'Always';
    return schedule.windows.map(w => `${describeDays(w.days)} ${w.start}–${w.end}`).join('; ');
}
/** What a device actually follows: its overrides over the organization's. */
export function effectivePolicy(controls, defaults) {
    return {
        paused: controls.paused,
        ends_at: controls.ends_at,
        timezone: controls.timezone ?? defaults.timezone,
        schedule: controls.schedule ?? defaults.schedule,
    };
}
/** A short line for the devices table. */
export function trackingSummary(controls, defaults, nowMs = Date.now()) {
    const policy = effectivePolicy(controls, defaults);
    if (policy.ends_at && Date.parse(policy.ends_at) <= nowMs) {
        return { label: 'Ended', variant: 'secondary' };
    }
    if (policy.paused)
        return { label: 'Paused', variant: 'warning' };
    const scheduled = policy.schedule && policy.schedule.mode === 'windows';
    const base = scheduled ? describeSchedule(policy.schedule) : 'Always';
    const until = policy.ends_at
        ? ` · until ${new Date(policy.ends_at).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
        })}`
        : '';
    return { label: base + until, variant: scheduled || policy.ends_at ? 'info' : 'success' };
}
/** IANA zone names this browser knows, for the time zone pickers. */
export function timezoneOptions() {
    const intl = Intl;
    if (typeof intl.supportedValuesOf === 'function') {
        try {
            const zones = intl.supportedValuesOf('timeZone');
            // Some browsers leave UTC out of the list; the Hub accepts it.
            return zones.includes('UTC') ? zones : ['UTC', ...zones];
        }
        catch {
            // Fall through to the short list.
        }
    }
    return ['UTC', Intl.DateTimeFormat().resolvedOptions().timeZone].filter(Boolean);
}
/** "UTC−5", "UTC+5:30" for `zone` at `nowMs`, or "" if the browser can't say. */
export function utcOffsetLabel(zone, nowMs = Date.now()) {
    try {
        const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
            .formatToParts(new Date(nowMs))
            .find(p => p.type === 'timeZoneName');
        if (!part)
            return '';
        // "GMT-5" → "UTC−5"; "GMT" or "GMT+0" is UTC itself.
        const label = part.value.replace(/^GMT/, 'UTC').replace('-', '−');
        return /^UTC([+−]0(:00)?)?$/.test(label) ? 'UTC' : label;
    }
    catch {
        return '';
    }
}
/** Shown first, under plain names: where most of our teams work. */
const FEATURED_ZONES = [
    {
        label: 'United States',
        zones: [
            ['America/New_York', 'New York · Eastern'],
            ['America/Chicago', 'Chicago · Central'],
            ['America/Denver', 'Denver · Mountain'],
            ['America/Phoenix', 'Phoenix · Mountain, no daylight saving'],
            ['America/Los_Angeles', 'Los Angeles · Pacific'],
            ['America/Anchorage', 'Anchorage · Alaska'],
            ['Pacific/Honolulu', 'Honolulu · Hawaii'],
        ],
    },
    {
        label: 'Australia',
        zones: [
            ['Australia/Sydney', 'Sydney · Eastern (NSW, ACT)'],
            ['Australia/Melbourne', 'Melbourne · Eastern (Victoria)'],
            ['Australia/Brisbane', 'Brisbane · Queensland, no daylight saving'],
            ['Australia/Hobart', 'Hobart · Tasmania'],
            ['Australia/Adelaide', 'Adelaide · Central (South Australia)'],
            ['Australia/Darwin', 'Darwin · Northern Territory'],
            ['Australia/Perth', 'Perth · Western Australia'],
        ],
    },
];
function withOffset(text, zone, nowMs) {
    const offset = utcOffsetLabel(zone, nowMs);
    return offset ? `${text} (${offset})` : text;
}
/** Time zones for the picker: the United States and Australia first, under
 * plain names, then every other zone grouped by region ("Africa",
 * "America", …), each with its current UTC offset. `keep` is a saved zone
 * to list even if this browser doesn't know it, so editing never drops it. */
export function timezoneGroups(keep = null, nowMs = Date.now()) {
    const featured = FEATURED_ZONES.map(group => ({
        label: group.label,
        options: group.zones.map(([value, text]) => ({ value, text: withOffset(text, value, nowMs) })),
    }));
    const featuredValues = new Set(FEATURED_ZONES.flatMap(g => g.zones.map(([value]) => value)));
    const zones = timezoneOptions();
    if (keep && !zones.includes(keep))
        zones.push(keep);
    const groups = new Map();
    for (const zone of zones) {
        if (featuredValues.has(zone))
            continue;
        const slash = zone.indexOf('/');
        const region = slash === -1 ? 'Other' : zone.slice(0, slash);
        const place = (slash === -1 ? zone : zone.slice(slash + 1))
            .replace(/_/g, ' ')
            .replace(/\//g, ' / ');
        const options = groups.get(region) ?? [];
        options.push({ value: zone, text: withOffset(place, zone, nowMs) });
        groups.set(region, options);
    }
    const regions = [...groups.entries()]
        .sort(([a], [b]) => (a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b)))
        .map(([label, options]) => ({
        label,
        options: options.sort((a, b) => a.text.localeCompare(b.text)),
    }));
    return [...featured, ...regions];
}
function searchable(text) {
    return text.toLowerCase().replace(/[_/]/g, ' ');
}
/** The groups narrowed to zones whose name, ID or offset contains every
 * word of `query` ("new york", "sydney", "utc+10"), empty groups dropped. */
export function filterTimezoneGroups(groups, query) {
    const words = searchable(query).split(/\s+/).filter(Boolean);
    if (words.length === 0)
        return groups;
    return groups
        .map(group => ({
        label: group.label,
        options: group.options.filter(option => {
            const haystack = `${searchable(option.text)} ${searchable(option.value)} ${searchable(group.label)}`;
            return words.every(word => haystack.includes(word));
        }),
    }))
        .filter(group => group.options.length > 0);
}
/** An ISO instant as the value of an `<input type="datetime-local">`, in this
 * browser's time. */
export function toLocalInput(iso) {
    if (!iso)
        return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return '';
    const pad = (n) => String(n).padStart(2, '0');
    return (`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`);
}
/** The reverse of {@link toLocalInput}; blank means no end date. */
export function fromLocalInput(value) {
    if (!value)
        return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
}
